import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SearchPage } from './pages/SearchPage.jsx';
import { AuftragLayout } from './pages/AuftragLayout.jsx';
import { Overview } from './pages/Overview.jsx';
import { Capture } from './pages/Capture.jsx';
import { Plan } from './pages/Plan.jsx';
import { Kunde } from './pages/Kunde.jsx';
import { Comments } from './pages/Comments.jsx';
import { Files } from './pages/Files.jsx';
import { Bewaesserungsplan, Plaene } from './pages/Bewaesserungsplan.jsx';
import { FormularEditorSeite, FormularListeSeite, UebersichtslisteEditor, UebersichtslisteListe } from './formulare/Seiten.jsx';
import { Mehr } from './pages/Mehr.jsx';
import { LoginScreen } from './pages/LoginScreen.jsx';
import { Verwaltung } from './pages/Verwaltung.jsx';
import { Apps } from './pages/Apps.jsx';
import { Freigabe } from './pages/Freigabe.jsx';
import { ModulHinweis } from './apps/ModulHinweis.jsx';
import { FormularSync } from './state/formularSync.jsx';
import { useAuth } from './state/auth.jsx';
import { useFunktionen } from './hooks/useFunktionen.js';
import { OutboxSync } from './state/outbox.jsx';
import { Vorladen } from './state/vorladen.jsx';
import { ExxasPause } from './state/exxasPause.jsx';

/**
 * Bereiche eines Auftrags – Reihenfolge = Seitenleiste (Tablet/Desktop). `label`/`long` sind Schlüssel im Wörterbuch
 * (useBegriffe), damit eine Firma oder Branche sie umbenennen kann.
 */
export const TABS = [
  { path: '', label: 'tab.uebersicht', long: 'baustellenuebersicht', icon: 'home' },
  { path: 'zeit-material', label: 'tab.erfassen', long: 'tab.zeitMaterial', icon: 'clock', funktion: ['zeit', 'material'] },
  { path: 'zeitplan', label: 'tab.zeitplan', long: 'tab.zeitplan', icon: 'cal', funktion: ['zeitplan'] },
  { path: 'kunde', label: 'tab.kunde', long: 'tab.kundeninfos', icon: 'user', funktion: ['kundeninfos'] },
  { path: 'kommentare', label: 'tab.notizen', long: 'tab.fotosNotizen', icon: 'camera', funktion: ['kommentare'] },
  { path: 'dateien', label: 'tab.dateien', long: 'tab.dateienBilder', icon: 'folder', funktion: ['dateien'] },
];

/**
 * Untere Leiste auf dem Handy: höchstens vier Ziele (Material 3 bis 5, Recherche reports/App Design 2026).
 * Alles Weitere liegt unter «Mehr» als grosse Kacheln.
 */
export const MOBIL_TABS = [
  { path: '', label: 'tab.uebersicht', icon: 'home' },
  { path: 'zeit-material', label: 'tab.erfassen', icon: 'clock', funktion: ['zeit', 'material'] },
  { path: 'kommentare', label: 'tab.notizen', icon: 'camera', funktion: ['kommentare'] },
  { path: 'mehr', label: 'tab.mehr', icon: 'mehr', unter: ['mehr', 'kunde', 'dateien', 'zeitplan', 'checkliste', 'plaene', 'formulare'] },
];

/** Bereich nur zeigen, wenn die Firma (eine der) Funktionen hat – sonst zurück zur Übersicht des Auftrags. */
function Nur({ funktion, children }) {
  const hat = useFunktionen();
  return [].concat(funktion).some(hat) ? children : <Navigate to=".." replace />;
}

export function App() {
  const auth = useAuth();
  // Freigabe-Link aus Teams (ki|partner): ohne Anmeldung, ohne App-Rahmen und ohne Hintergrund-Abfragen
  if (useLocation().pathname === '/freigabe') return <Freigabe />;
  return (
    <>
      <div aria-hidden={auth.required || undefined} style={{ display: 'contents' }}>
        <AppRoutes />
      </div>
      <OutboxSync />
      <FormularSync />
      {!auth.required && <ExxasPause />}
      {!auth.required && <Vorladen />}
      {!auth.required && <ModulHinweis />}
      {auth.required && <LoginScreen />}
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<SearchPage />} />
      <Route path="/verwaltung" element={<Verwaltung />} />
      <Route path="/apps" element={<Apps />} />
      <Route path="/auftrag/:nr" element={<AuftragLayout />}>
        <Route index element={<Overview />} />
        <Route path="mehr" element={<Mehr />} />
        <Route path="zeit-material" element={<Nur funktion={['zeit', 'material']}><Capture /></Nur>} />
        <Route path="zeitplan" element={<Nur funktion="zeitplan"><Plan /></Nur>} />
        <Route path="kunde" element={<Nur funktion="kundeninfos"><Kunde /></Nur>} />
        <Route path="kommentare" element={<Nur funktion="kommentare"><Comments /></Nur>} />
        {/* Übersichtsliste: neuer Formular-Editor oder bisherige Seite (FORMULARE_NEU) – gleiche Adressen, gleiche Dateien */}
        <Route path="checkliste" element={<Nur funktion="uebersichtsliste"><UebersichtslisteListe /></Nur>} />
        <Route path="checkliste/:dateiId" element={<Nur funktion="uebersichtsliste"><UebersichtslisteEditor /></Nur>} />
        {/* weitere Formulare aus Vorlagen (z. B. Abnahme); ob die Firma sie hat, prüft /api/formulare/{id} */}
        <Route path="formulare/:vorlage" element={<FormularListeSeite />} />
        <Route path="formulare/:vorlage/:dateiId" element={<FormularEditorSeite />} />
        <Route path="plaene" element={<Nur funktion="bewaesserungsplan"><Plaene /></Nur>} />
        <Route path="plaene/:dateiId" element={<Nur funktion="bewaesserungsplan"><Bewaesserungsplan /></Nur>} />
        <Route path="dateien/*" element={<Nur funktion="dateien"><Files /></Nur>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
